import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import StepIndicator from "./components/StepIndicator";
import CityStep from "./components/CityStep";
import ServiceStep from "./components/ServiceStep";
import DateStep from "./components/DateStep";
import AddonsStep from "./components/AddonsStep";
import CheckoutStep from "./components/CheckoutStep";

const TOTAL_STEPS = 5;

interface BookingState {
  city: string;
  service: string;
  date: string;
  addons: string[];
  photographer_preference: "none" | "female_preferred";
}

export default function BookingPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialCity = searchParams.get("city") || "";
  const [step, setStep] = useState(initialCity ? 2 : 1);
  const [confirmed, setConfirmed] = useState(false);
  const [booking, setBooking] = useState<BookingState>({
    city: initialCity,
    service: "",
    date: "",
    addons: [],
    photographer_preference: "none",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const handleNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const toggleAddon = (id: string) => {
    setBooking((prev) => ({
      ...prev,
      addons: prev.addons.includes(id)
        ? prev.addons.filter((a) => a !== id)
        : [...prev.addons, id],
    }));
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#F7F6F3] flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6 py-24">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-[#C8B8A6]/20 flex items-center justify-center mx-auto mb-8">
              <i className="ri-check-line text-[#C8B8A6] text-2xl" />
            </div>
            <h1 className="text-3xl font-extralight text-[#2C2C2C] mb-4" style={{ letterSpacing: "-0.02em" }}>
              {t("tabifoto.booking.booking_confirmed")}
            </h1>
            <p className="text-sm text-[#2C2C2C]/50 font-light leading-relaxed mb-10">
              {t("tabifoto.booking.booking_confirmed_desc")}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#2C2C2C]/50 hover:text-[#2C2C2C] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex flex-col">
      <Navbar />

      {/* Page header */}
      <div className="pt-28 pb-12 px-6 text-center">
        <p className="text-xs uppercase tracking-[0.4em] text-[#C8B8A6] mb-3">
          Booking
        </p>
        <h1 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C]" style={{ letterSpacing: "-0.02em" }}>
          {t("tabifoto.booking.title")}
        </h1>
      </div>

      {/* Step indicator */}
      <div className="pb-12">
        <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />
      </div>

      {/* Step content */}
      <main className="flex-1 pb-20">
        {step === 1 && (
          <CityStep
            selected={booking.city}
            onSelect={(city) => setBooking((b) => ({ ...b, city }))}
            onNext={handleNext}
          />
        )}
        {step === 2 && (
          <ServiceStep
            selected={booking.service}
            onSelect={(service) => setBooking((b) => ({ ...b, service }))}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 3 && (
          <DateStep
            selected={booking.date}
            onSelect={(date) => setBooking((b) => ({ ...b, date }))}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 4 && (
          <AddonsStep
            selected={booking.addons}
            onToggle={toggleAddon}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 5 && (
          <CheckoutStep
            bookingData={booking}
            onBack={handleBack}
            onConfirm={() => setConfirmed(true)}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}
